// apps/alivia/gpa.js — the GPA tracker's math (H·3, ported from Estelle's H·2 gpa.js). GPA is
// MANUAL-ENTRY per course: she picks a letter grade; nothing is computed from assignment scores
// (that needs weighting schemes her brief doesn't define — flagged as future).
//
// The one settled per-app difference: Alivia's GPA is CREDIT-WEIGHTED — every course carries
// credit hours (default 3), and GPA = Σ(points × credits) / Σ credits, the standard college 4.0.
// This difference is exactly why GPA math is app-body code, not core.
//
// The standard 4.0 map lives in core (X·3 — one owner; a scale correction was a two-copy fix);
// re-exported here so the server and the bundle keep their one import site.
import { GRADE_POINTS, GRADES } from "@school-apps/core/hub/grade-points";
export { GRADE_POINTS, GRADES };
export const DEFAULT_CREDITS = 3;

/** A usable credit-hours value, defaulting old/absent/invalid payloads to 3. */
export function creditsOf(course) {
  const c = course?.credits;
  return typeof c === "number" && Number.isFinite(c) && c > 0 ? c : DEFAULT_CREDITS;
}

/**
 * Credit-weighted GPA over the courses that have a grade entered; null when none do (graceful
 * "no grades yet" — never a fake 0.0).
 * @param {Array<{ grade?: string, credits?: number }>} courses
 * @returns {number|null}
 */
export function gpaOf(courses) {
  const graded = courses.filter((c) => typeof c.grade === "string" && c.grade in GRADE_POINTS);
  if (graded.length === 0) return null;
  let points = 0, hours = 0;
  for (const c of graded) {
    const cr = creditsOf(c);
    points += GRADE_POINTS[c.grade] * cr;
    hours += cr;
  }
  return points / hours;
}

/** Display form: two decimals, or an em-dash when there's nothing to average. */
export function fmtGpa(value) {
  return value === null ? "—" : value.toFixed(2);
}
